import { useEffect, useMemo, useState } from 'react';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Progress } from '@xiltrion/ui/progress';
import { Spinner } from '@xiltrion/ui/spinner';
import { Timeline, TimelineHeader, TimelineIndicator, TimelineItem, TimelineSeparator, TimelineTitle } from '@xiltrion/ui/timeline';

interface Etapa { titulo: string; detalle: string; icono: NombreIcono; ms: number }

/**
 * Presentación de la verificación de integridad: recorre en pantalla las etapas del protocolo de auditoría
 * (índice, estructura, firma, huellas, informe) mientras la verificación real corre en el servidor. El ritmo
 * es de presentación; el resultado que se muestra al final es siempre el real. Avisa con `onTerminar`.
 */
export function IntegrityCheckAnimation({ fases, documentos, listo, onTerminar }: { fases: number; documentos: number; listo: boolean; onTerminar: () => void }) {
  const etapas = useMemo<Etapa[]>(() => [
    { titulo: 'Leyendo el índice firmado', detalle: `${fases > 1 ? `${fases} fases · ` : ''}${documentos} ${documentos === 1 ? 'documento' : 'documentos'}`, icono: 'mdi:file-document-outline', ms: 500 },
    { titulo: 'Nivel 1 · Estructura del índice', detalle: 'Metadatos completos y numeración sin saltos ni repetidos', icono: 'mdi:format-list-bulleted', ms: 650 },
    { titulo: 'Nivel 2 · Firma digital', detalle: 'Certificado, estado de revocación y estampa de tiempo', icono: 'mdi:file-certificate-outline', ms: 750 },
    { titulo: 'Nivel 3 · Huella de cada archivo', detalle: 'Se recalcula el SHA-256 y se compara con el índice', icono: 'mdi:key-outline', ms: 900 },
    { titulo: 'Preparando el informe', detalle: 'Lista de control del auditor', icono: 'mdi:clipboard-list-outline', ms: 400 },
  ], [fases, documentos]);
  const total = etapas.reduce((a, e) => a + e.ms, 0);
  const [transcurrido, setTranscurrido] = useState(0);

  useEffect(() => {
    const inicio = performance.now();
    let frame = 0;
    const tick = (t: number) => {
      const ms = Math.min(total, t - inicio);
      setTranscurrido(ms);
      if (ms < total) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [total]);

  // La última etapa se queda abierta hasta que llegue el resultado real.
  const terminada = transcurrido >= total;
  useEffect(() => { if (terminada && listo) onTerminar(); }, [terminada, listo, onTerminar]);

  let acumulado = 0;
  let actual = etapas.length - 1;
  for (const [i, e] of etapas.entries()) {
    if (transcurrido < acumulado + e.ms) { actual = i; break; }
    acumulado += e.ms;
  }
  const progreso = Math.round((transcurrido / total) * (listo ? 100 : 96));
  const etapa = etapas[actual]!;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col items-center gap-3 pt-2 text-center">
        <div className="relative size-20">
          <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" aria-hidden />
          <span className="absolute -inset-3 rounded-full border border-primary/20 animate-pulse" aria-hidden />
          <div className="relative flex size-20 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30">
            <Icono nombre={etapa.icono} className="size-9" aria-hidden />
          </div>
        </div>
        <div role="status" aria-live="polite">
          <div className="font-semibold text-foreground">{etapa.titulo}</div>
          <div className="text-sm text-muted-foreground">{etapa.detalle}</div>
        </div>
        <div className="w-full max-w-sm">
          <Progress value={progreso} aria-label="Avance de la verificación" />
          <div className="mt-1 text-xs text-muted-foreground tnum">{progreso} %</div>
        </div>
      </div>


      <Timeline value={actual} orientation="vertical" role="list" aria-label="Etapas de la verificación">
        {etapas.map((e, i) => {
          const hecho = i < actual || (terminada && listo);
          const enCurso = i === actual && !hecho;
          return (
            <TimelineItem key={e.titulo} step={i + 1} role="listitem" className="group-data-[orientation=vertical]/timeline:ms-9 group-data-[orientation=vertical]/timeline:not-last:pb-3">
              <TimelineHeader>
                <TimelineSeparator className="bg-border! group-data-[orientation=vertical]/timeline:-left-6 group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                <TimelineTitle className={`text-sm ${hecho || enCurso ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {e.titulo}
                  {i === 3 && hecho && documentos > 0 && <span className="ml-1 font-normal text-muted-foreground tnum">· {documentos} {documentos === 1 ? 'archivo' : 'archivos'}</span>}
                </TimelineTitle>
                <TimelineIndicator className={`flex size-6 items-center justify-center border-2 group-data-[orientation=vertical]/timeline:-left-6 ${hecho ? 'border-success bg-success text-success-foreground group-data-[completed]/timeline-item:border-success' : enCurso ? 'border-primary bg-primary text-primary-foreground group-data-[completed]/timeline-item:border-primary' : 'border-border bg-background text-muted-foreground group-data-[completed]/timeline-item:border-border'}`}>
                  {hecho ? <Icono nombre="mdi:check" className="size-4" aria-label="hecho" /> : enCurso ? <Spinner className="size-3.5" /> : <span className="text-[0.65rem] font-semibold tnum">{i + 1}</span>}
                </TimelineIndicator>
              </TimelineHeader>
            </TimelineItem>
          );
        })}
      </Timeline>
    </div>
  );
}
